import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DownloadSimple, X } from '@phosphor-icons/react';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmt = (v) => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-';

/**
 * Generic drill-down dialog for Analytics charts. Given a dimension + value + date
 * range it fetches /analytics/drill and lists the underlying invoice / line-item rows
 * behind that chart segment, with a CSV export.
 */
export default function AnalyticsDrillDialog({ spec, startDate, endDate, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!spec) { setData(null); return; }
    (async () => {
      setLoading(true);
      setData(null);
      try {
        const { data: res } = await api.get('/analytics/drill', {
          params: { dimension: spec.dimension, value: spec.value, start_date: startDate, end_date: endDate },
        });
        setData(res);
      } catch { toast.error('Failed to load drill-down data'); }
      finally { setLoading(false); }
    })();
  }, [spec, startDate, endDate]);

  const isItem = data?.row_type === 'item';
  const isPayment = data?.row_type === 'payment';

  const exportCsv = () => {
    if (!data?.rows?.length) { toast.error('Nothing to export'); return; }
    const cols = isItem
      ? [['date', 'Date'], ['invoice_number', 'Invoice #'], ['customer_name', 'Customer'], ['clinic', 'Branch'], ['product', 'Product'], ['category', 'Category'], ['quantity', 'Qty'], ['amount', 'Amount'], ['serials', 'Serials']]
      : isPayment
        ? [['date', 'Date'], ['payment_number', 'Payment #'], ['invoice_number', 'Invoice #'], ['customer_name', 'Customer'], ['clinic', 'Branch'], ['mode', 'Mode'], ['amount', 'Amount']]
        : [['date', 'Date'], ['invoice_number', 'Invoice #'], ['customer_name', 'Customer'], ['clinic', 'Branch'], ['audiologist', 'Audiologist'], ['lead_source', 'Lead Source'], ['status', 'Status'], ['amount', 'Amount'], ['balance', 'Balance']];
    const head = cols.map(c => c[1]).join(',');
    const body = data.rows.map(r => cols.map(c => {
      const v = r[c[0]] ?? '';
      return typeof v === 'string' && (v.includes(',') || v.includes('"')) ? `"${v.replace(/"/g, "'")}"` : v;
    }).join(',')).join('\n');
    const blob = new Blob([head + '\n' + body], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `drilldown_${spec.dimension}_${String(spec.value).replace(/[^a-z0-9]+/gi, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog open={!!spec} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="analytics-drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-6">
            <span>{spec?.label || spec?.value}</span>
            {data && (
              <Button size="sm" variant="outline" onClick={exportCsv} data-testid="drill-export-btn">
                <DownloadSimple size={14} className="mr-1" /> Export CSV
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-16 text-center"><div className="inline-block w-6 h-6 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></div>
        ) : !data ? null : (
          <>
            <div className="flex items-center gap-4 text-xs text-[#64748B] pb-2 border-b border-[#E2E8F0]">
              <span data-testid="drill-count"><b className="text-[#0F172A]">{data.count}</b> record{data.count === 1 ? '' : 's'}{data.truncated ? ` · showing first ${data.shown}` : ''}</span>
              <span data-testid="drill-total">Total: <b className="text-[#0F4C81]">{fmt(data.total_amount)}</b></span>
              {isItem && <span>Units: <b className="text-[#0F172A]">{data.total_qty}</b></span>}
            </div>
            <div className="overflow-auto flex-1 -mx-1 px-1">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#0F4C81] text-white text-[10px] uppercase z-10">
                  {isItem ? (
                    <tr>
                      <th className="text-left p-2">Date</th><th className="text-left p-2">Invoice #</th>
                      <th className="text-left p-2">Customer</th><th className="text-left p-2">Branch</th>
                      <th className="text-left p-2">Product</th><th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Amount</th>
                    </tr>
                  ) : isPayment ? (
                    <tr>
                      <th className="text-left p-2">Date</th><th className="text-left p-2">Payment #</th>
                      <th className="text-left p-2">Invoice #</th><th className="text-left p-2">Customer</th>
                      <th className="text-left p-2">Branch</th><th className="text-left p-2">Mode</th>
                      <th className="text-right p-2">Amount</th>
                    </tr>
                  ) : (
                    <tr>
                      <th className="text-left p-2">Date</th><th className="text-left p-2">Invoice #</th>
                      <th className="text-left p-2">Customer</th><th className="text-left p-2">Branch</th>
                      <th className="text-left p-2">Audiologist</th><th className="text-left p-2">Status</th>
                      <th className="text-right p-2">Amount</th><th className="text-right p-2">Balance</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    isPayment ? (
                      <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`drill-row-${i}`}>
                        <td className="p-2 whitespace-nowrap">{r.date}</td>
                        <td className="p-2 font-mono">{r.payment_number || '-'}</td>
                        <td className="p-2 font-mono">{r.invoice_number || '-'}</td>
                        <td className="p-2">{r.customer_name || '-'}</td>
                        <td className="p-2">{r.clinic || '-'}</td>
                        <td className="p-2 uppercase">{r.mode || '-'}</td>
                        <td className="p-2 text-right font-semibold">{fmt(r.amount)}</td>
                      </tr>
                    ) : (
                      <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`drill-row-${i}`}>
                        <td className="p-2 whitespace-nowrap">{r.date}</td>
                        <td className="p-2 font-mono">{r.invoice_number || '-'}</td>
                        <td className="p-2">{r.customer_name || '-'}</td>
                        <td className="p-2">{r.clinic || '-'}</td>
                        {isItem ? (
                          <>
                            <td className="p-2">{r.product || '-'}</td>
                            <td className="p-2 text-right">{r.quantity}</td>
                            <td className="p-2 text-right font-semibold">{fmt(r.amount)}</td>
                          </>
                        ) : (
                          <>
                            <td className="p-2">{r.audiologist || '-'}</td>
                            <td className="p-2 capitalize">{(r.status || '').replace(/_/g, ' ')}</td>
                            <td className="p-2 text-right font-semibold">{fmt(r.amount)}</td>
                            <td className="p-2 text-right text-[#DC2626]">{fmt(r.balance)}</td>
                          </>
                        )}
                      </tr>
                    )
                  ))}
                  {!data.rows.length && (
                    <tr><td colSpan={isItem || isPayment ? 7 : 9} className="text-center py-8 text-[#64748B]">No records behind this segment</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
